import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import LoadingSpinner from '../components/LoadingSpinner';
import MilestoneDates from '../components/MilestoneDates';
import ContractInfo from '../components/ContractInfo';
import ContractEmails from '../components/ContractEmails';
import TaskList from '../components/TaskList';
import EmailAttachments from '../components/EmailAttachments';
import TransactionTeam from '../components/TransactionTeam';
import BrokerageChecklist from '../components/BrokerageChecklist';
import { ComplianceIssueItems } from '../components/ComplianceIssueBar';
import useViewerSubscription from '../hooks/useViewerSubscription';
import './ContractDetail.css';
import Logo from '../components/Logo';
import GenerateFormControl from '../components/GenerateFormControl';
import ChatBox from '../components/ChatBox';
import { getContractApiUrl, getDealReplaceApiUrl } from '../utils/apiUrls';
import { portalLoginAuthorizationParams } from '../utils/authSession';
import DealReplaceConfirmModal from '../components/DealReplaceConfirmModal';
import {
  createFormTypeFromSearch,
  dealReplaceParamsFromSearch,
  stripDealReplaceParams,
} from '../utils/dealReplaceConfirm';

// Backend URL configuration
const BACKEND_URL = getContractApiUrl();

const ContractDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout, loginWithRedirect, getAccessTokenSilently, getIdTokenClaims } = useAuth0();
  const location = useLocation();
  const [contract, setContract] = useState(null);
  const [milestoneDates, setMilestoneDates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [complianceIssues, setComplianceIssues] = useState([]);
  const [overdueTasks, setOverdueTasks] = useState([]);
  const { isGuest: isGuestViewer, canResolveCompliance } = useViewerSubscription();

  // Section collapse states
  const [sectionStates, setSectionStates] = useState({
    milestones: true,
    team: false,
    tasks: false,
    contractInfo: false,
    emails: false,
    emailAttachments: false,
    brokerageChecklist: false,
  });

  useEffect(() => {
    if (isAuthenticated && id) {
      fetchContractData();
    }
  }, [isAuthenticated, id]);

  const fetchContractData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch both contract details and milestone dates
      const [contractResponse, milestonesResponse, complianceResponse, tasksResponse] = await Promise.all([
        axios.get(`${BACKEND_URL}/contracts/${id}`, {
          params: { user_email: user?.email }
        }),
        axios.get(`${BACKEND_URL}/contracts/${id}/milestone-dates?user_email=${user?.email}`),
        axios.get(`${BACKEND_URL}/compliance-issues?contract_id=${id}&limit=200&user_email=${user?.email}`),
        axios.get(`${BACKEND_URL}/tasks?contract_id=${id}&status=to-do,in-progress&user_email=${user?.email}`)
      ]);

      setContract(contractResponse.data);
      setMilestoneDates(milestonesResponse.data);
      setComplianceIssues((complianceResponse.data.compliance_issues || []).filter((i) => i.status === 'open'));

      // Calculate overdue tasks
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tasks = tasksResponse.data.tasks || [];
      const overdue = tasks.filter(t => {
        if (!t.due_date) return false;
        const [y, m, d] = t.due_date.split('-').map(Number);
        const dueDate = new Date(y, m - 1, d);
        return dueDate < today;
      });
      setOverdueTasks(overdue);
    } catch (err) {
      console.error('Error fetching contract data:', err);
      if (err.response?.status === 404) {
        setError('Contract not found.');
      } else {
        setError('Failed to load contract data. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate('/');
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const toggleSection = (sectionName) => {
    setSectionStates(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const dealReplaceParams = dealReplaceParamsFromSearch(location.search);
  const startFormType = createFormTypeFromSearch(location.search, dealReplaceParams?.action);
  const hideDealReplaceModal = dealReplaceParams?.action === 'draft_yes'
    || dealReplaceParams?.action === 'draft_no';

  useEffect(() => {
    if (dealReplaceParams?.action !== 'draft_yes' || !dealReplaceParams.token) {
      return undefined;
    }
    axios.post(
      `${getDealReplaceApiUrl()}/${dealReplaceParams.token}/resolve`,
      { action: 'draft_yes', portal_wizard: true },
      { headers: { Accept: 'application/json' } },
    ).catch(() => {});
    return undefined;
  }, [dealReplaceParams?.action, dealReplaceParams?.token]);

  const dealReplaceModal = hideDealReplaceModal ? null : (
    <DealReplaceConfirmModal
      search={location.search}
      address={milestoneDates?.property_address || contract?.property_address}
    />
  );

  if (!isAuthenticated) {
    return (
      <div className="auth-container">
        <div className="auth-content">
          <h1>Access Denied</h1>
          <p>Please log in to view contract details</p>
          <button
            className="login-button"
            onClick={() => loginWithRedirect({
              appState: { returnTo: `${location.pathname}${location.search}` },
              authorizationParams: portalLoginAuthorizationParams(),
            })}
          >
            Go to Login
          </button>
        </div>
        {dealReplaceModal}
      </div>
    );
  }

  if (loading) {
    return (
      <>
        <LoadingSpinner />
        {dealReplaceModal}
      </>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <h2>Error</h2>
          <p>{error}</p>
          <div className="error-actions">
            <button onClick={fetchContractData} className="retry-button">
              Retry
            </button>
            <button onClick={handleBackClick} className="back-button">
              Back to Contracts
            </button>
          </div>
        </div>
        {dealReplaceModal}
      </div>
    );
  }

  const formatNames = (namesString) => {
    if (!namesString) return '';
    const names = namesString.split(',').map(n => n.trim());
    if (names.length === 0) return '';
    if (names.length === 1) return names[0];
    const last = names.pop();
    return `${names.join(', ')} & ${last}`;
  };

  const formatDealPrice = (value) => {
    if (value == null || value === '') return '';
    const amount = Number(String(value).replace(/[$,]/g, ''));
    if (!Number.isFinite(amount)) return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const isCancelled = contract?.deal_status === 'cancelled';
  const purchasePrice = formatDealPrice(contract?.purchase_price);

  return (
    <div className="contract-detail-container">
      <header className="app-header">
        <div className="header-content">
          <div className="header-left">
            <Logo size={72} variant="arrow" />
            <div className="header-title-section">
              {contract && milestoneDates ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <h1 className="property-address-title">{milestoneDates.property_address}</h1>
                    <span className={`detail-type-badge ${isCancelled ? 'detail-type-badge--cancelled' : 'detail-type-badge--contract'}`}>
                      {isCancelled ? 'Cancelled' : 'Under Contract'}
                    </span>
                  </div>
                  <div className="contract-parties-subtitle">
                    {formatNames(milestoneDates.seller_name)} → {formatNames(milestoneDates.buyer_name)}
                    {purchasePrice ? ` for ${purchasePrice}` : ''}
                  </div>
                </>
              ) : (
                <h1>Contract Details</h1>
              )}
            </div>
          </div>
          <div className="header-right" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <GenerateFormControl
              parentType="contract"
              parentId={id}
              userEmail={user?.email}
              representedSides={contract?.represented_sides}
              disabled={isGuestViewer}
              initialFormType={startFormType}
              onWizardClosed={() => {
                if (!startFormType) {
                  return;
                }
                navigate({
                  pathname: location.pathname,
                  search: stripDealReplaceParams(location.search),
                }, { replace: true });
              }}
            />
            <button className="back-button" onClick={handleBackClick}>
              ← Back to Contracts
            </button>
          </div>
        </div>
      </header>

      <main className="main-content">
        <ChatBox
          key={id ? `contract:${id}` : 'home'}
          userEmail={user?.email}
          userName={user?.name || user?.nickname}
          getAccessTokenSilently={getAccessTokenSilently}
          getIdTokenClaims={getIdTokenClaims}
          entityHint={id ? `contract:${id}` : undefined}
        />
        {contract && milestoneDates && (
          <>
            {/* #208: guest-mode headline banner for approved non-subscribers */}
            {isGuestViewer && (
              <div className="guest-access-banner">
                <span className="guest-access-banner-text">
                  You are using KeyFlo as a guest to the {milestoneDates.property_address} deal.
                  Click 'Get Full Access' to trial KeyFlo for your next transaction for free
                </span>
                <button className="guest-access-banner-button" onClick={() => navigate('/profile?upgrade=1')}>
                  Get Full Access
                </button>
              </div>
            )}

            {/* 0. Overdue Tasks and Compliance Issues */}
            {(overdueTasks.length > 0 || complianceIssues.length > 0) && (
              <section className="top-alerts-section">
                {overdueTasks.length > 0 && (
                  <div className="alert-banner overdue-banner">
                    <div className="alert-title overdue-title">⚠️ Overdue Tasks</div>
                    {overdueTasks.map(task => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const [y, m, d] = task.due_date.split('-').map(Number);
                      const dueDate = new Date(y, m - 1, d);
                      const diffTime = dueDate - today;
                      const daysOverdue = Math.abs(Math.ceil(diffTime / (1000 * 60 * 60 * 24)));

                      const formatDateShort = (dateStr) => {
                        const d = new Date(dateStr + 'T00:00:00');
                        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                      };

                      return (
                        <div key={task.id} className="task-row is-active is-overdue">
                          <div className="col-assignee">{task.responsible_party}</div>
                          <div className="col-task-name">
                            {task.task_name}
                            <span className="status-badge-overdue">
                              Over due by {daysOverdue} day{daysOverdue !== 1 ? 's' : ''}!
                            </span>
                          </div>
                          <div className="col-date">{formatDateShort(task.due_date)}</div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {complianceIssues.length > 0 && (
                  <div className="alert-banner compliance-banner">
                    <div className="alert-title compliance-title">⚠️ Action Required</div>
                    <ComplianceIssueItems
                      issues={complianceIssues}
                      canResolve={canResolveCompliance}
                      apiUrl={BACKEND_URL}
                      onResolved={(issue) => {
                        setComplianceIssues((prev) => prev.filter((item) => item.id !== issue.id));
                      }}
                    />
                  </div>
                )}
              </section>
            )}

            {/* 1. Key Milestones (Keep at top) */}
            <section className="milestone-section">
              <MilestoneDates
                data={milestoneDates}
                isExpanded={sectionStates.milestones}
                onToggle={() => toggleSection('milestones')}
                calendarLink={milestoneDates?.calendar_link}
              />
            </section>

            {/* 2. Brokerage Checklist (team members with own TMS sync, or super admins) */}
            <section>
              <BrokerageChecklist
                contractId={contract.id}
                isExpanded={sectionStates.brokerageChecklist}
                onToggle={() => toggleSection('brokerageChecklist')}
              />
            </section>

            {/* 3. Transaction Documents */}
            <section className="attachments-section">
              <EmailAttachments
                contractId={contract.id}
                propertyAddress={milestoneDates.property_address}
                canSendInkless={contract.can_inkless_send === true}
                canRecoverInkless={
                  contract.can_read_parent === true && contract.can_mutate === true
                }
                isExpanded={sectionStates.emailAttachments}
                onToggle={() => toggleSection('emailAttachments')}
              />
            </section>

            {/* 4. Task List */}
            <section>
              <TaskList
                contractId={contract.id}
                userEmail={user?.email}
                isExpanded={sectionStates.tasks}
                onToggle={() => toggleSection('tasks')}
              />
            </section>

            {/* 5. Key People */}
            <section className="team-section">
              <TransactionTeam
                contractId={contract.id}
                canManageTeam={contract.can_manage_team === true}
                canAssignProtectedTeamRoles={contract.can_assign_protected_team_roles === true}
                isExpanded={sectionStates.team}
                onToggle={() => toggleSection('team')}
              />
            </section>

            {/* 6. Email History */}
            <section className="emails-section">
              <ContractEmails
                contractId={contract.id?.toString() || id}
                userEmail={user?.email}
                isExpanded={sectionStates.emails}
                onToggle={() => toggleSection('emails')}
              />
            </section>

            {/* 7. Contract Details (includes Listing Details subsection when listing is linked) */}
            <section className="contract-info-section">
              <ContractInfo
                contract={contract}
                contractId={contract.id}
                listing={contract.listing || null}
                isExpanded={sectionStates.contractInfo}
                onToggle={() => toggleSection('contractInfo')}
              />
            </section>
          </>
        )}
      </main>
      {dealReplaceModal}
    </div>
  );
};

export default ContractDetail;
